import BackgroundGeolocation, {
  AuthorizationEvent,
  HeartbeatEvent,
  Location as BGLocation,
  MotionChangeEvent,
  ProviderChangeEvent,
} from 'react-native-background-geolocation';
import { BG_DISTANCE_FILTER_METERS } from '@/utils/backgroundTracking';
import AsyncStorage from '@react-native-async-storage/async-storage';
import { RECORDING_STORAGE_KEYS } from '@/utils/recordingSession';
import i18n from '@/i18n';

/**
 * Make the SDK's tracking state match the persisted recording session.
 *  - Active session + SDK stopped  -> start() + moving.  Resumes a live walk
 *    after a JS reload or a Samsung process kill — nothing else re-starts
 *    tracking in that state (the OEM-kill restart only fires on events).
 *  - No session + SDK enabled      -> stop().  Backstop for the battery-drain
 *    class: `stopOnTerminate: false` persists an enabled=true from a
 *    never-stopped/killed walk (and `startOnBoot: true` resumes it after
 *    reboots), so without this the FGS keeps polling GPS at 1 Hz forever.
 *
 * Runs at the end of every initBackgroundTracking() (record + follow tabs), so
 * "background GPS on " tracking on "trail session active" is an invariant.
 */
export async function reconcileTrackingState() {
  try {
    const [sessionActive, state] = await Promise.all([
      AsyncStorage.getItem(RECORDING_STORAGE_KEYS.START_TIME),
      BackgroundGeolocation.getState() as any,
    ]);
    if (sessionActive) {
      if (!state.enabled) {
        await BackgroundGeolocation.start();
        await BackgroundGeolocation.changePace(true);
      }
    } else if (state.enabled) {
      await BackgroundGeolocation.stop();
    }
  } catch (e) {
    console.warn('[BG] reconcileTrackingState failed:', e);
  }
}

type InitBackgroundTrackingOptions = {
  onLocation: (location: BGLocation) => void;
  onLocationError?: (error: any) => void;
  onMotionChange?: (event: MotionChangeEvent) => void;
  onProviderChange?: (event: ProviderChangeEvent) => void;
  onAuthorization?: (event: AuthorizationEvent) => void;
  /** Called when a heartbeat fires — useful for detecting the service is still alive. */
  onHeartbeat?: (event: HeartbeatEvent) => void;
  /** Called when the SDK's enabled state changes (e.g. service killed by OEM). */
  onEnabledChange?: (enabled: boolean) => void;
  /** Called when the device enters/leaves power-saving mode (Samsung battery restriction). */
  onPowerSaveChange?: (isPowerSave: boolean) => void;
};

export async function initBackgroundTracking(options: InitBackgroundTrackingOptions) {
  // Register JS listeners immediately so native events won't be sent with
  // "no listeners registered" if the native side emits before `ready()` completes.
  BackgroundGeolocation.onLocation(options.onLocation, options.onLocationError);

  if (options.onMotionChange) {
    BackgroundGeolocation.onMotionChange(options.onMotionChange);
  }

  if (options.onProviderChange) {
    BackgroundGeolocation.onProviderChange(options.onProviderChange);
  }

  if (options.onAuthorization) {
    BackgroundGeolocation.onAuthorization(options.onAuthorization);
  }

  if (options.onHeartbeat) {
    BackgroundGeolocation.onHeartbeat(options.onHeartbeat);
  }

  if (options.onEnabledChange) {
    BackgroundGeolocation.onEnabledChange(options.onEnabledChange);
  }

  if (options.onPowerSaveChange) {
    BackgroundGeolocation.onPowerSaveChange(options.onPowerSaveChange);
  }

  // Then configure the plugin. `ready()` is async and may take time; listeners
  // are already attached above to avoid race conditions.
  await BackgroundGeolocation.ready({
    // Android: request 'Always'. The previous 'Any' strategy granted only
    // foreground location on fresh installs (nothing ever showed the standard
    // "Allow all the time" increment dialog), and Samsung One UI throttles a
    // while-in-use location foreground-service with the screen off — walks
    // "started" but recorded nothing. 'Always' + the explicit background-permission
    // request in startRecordingShared fixes that. The SDK's own nagging alert is
    // disabled below (`disableLocationAuthorizationAlert`) so One UI's
    // shouldShowRationale loop cannot reopen the old 10-minute dialog spam.
    locationAuthorizationRequest: 'Always',
    // Stop the SDK's custom "Allow all the time?" dialog from re-appearing
    // whenever it deems background access missing (Samsung One UI reports it
    // as missing repeatedly); background access is granted once via the OS
    // increment dialog in startRecordingShared instead.
    disableLocationAuthorizationAlert: true,
    desiredAccuracy: (BackgroundGeolocation as any).DESIRED_ACCURACY_HIGH,
    distanceFilter: BG_DISTANCE_FILTER_METERS, // 0 = no filtering, record all locations
    // Android-specific settings to prevent tracking from stopping
    locationUpdateInterval: 1000, // Request updates every 1 second
    fastestLocationUpdateInterval: 500, // Allow updates as fast as every 500ms
    deferTime: 0, // Don't defer/batch location updates

    // ─── Critical OEM-survival settings ────────────────────────────────
    // foregroundService: true makes the tracking service MUCH more resistant
    // to being killed by aggressive OEM battery optimisation (Samsung, Xiaomi,
    // Huawei, etc.).  On Android 8+ it's enforced by the SDK regardless, but
    // setting it explicitly documents intent and ensures the persistent
    // notification is shown with our custom strings.
    foregroundService: true,

    // enableHeadless allows the plugin to continue operating even when the
    // main React Native activity is terminated by the OS (common on Samsung).
    // The native Android service persists and records to its SQLite DB.  When
    // the user re-opens the app, recorded locations are synced back to JS.
    enableHeadless: true,

    // Heartbeat fires every 60 s (Android minimum) while the plugin is in
    // stationary state.  This keeps the service "alive" in the eyes of the OS
    // and gives us a hook to detect when tracking has been disrupted.
    heartbeatInterval: 60,

    // Disable elasticity — we want every point, not fewer points at speed.
    // Also prevents the SDK from reducing update frequency, which Samsung
    // already does aggressively via its battery optimisation.
    disableElasticity: true,

    // ─── Keep tracking alive across app lifecycle ─────────────────────
    stopOnTerminate: false,   // Continue tracking after user terminates the app
    startOnBoot: true,        // Auto-start tracking after device reboot (resume an ongoing walk)

    // Prevent SDK from going into stationary mode too aggressively.
    // Setting to 0 means: never auto-stop, only the user can stop tracking.
    stopTimeout: 0,

    // Activity recognition — use most aggressive setting so the SDK always
    // considers itself "moving" while we're actively recording.
    activityRecognitionType: (BackgroundGeolocation as any).ACTIVITY_TYPE_OTHER,
    isMoving: true, // Start in moving state immediately

    // Without this, a missing ACTIVITY_RECOGNITION runtime permission makes
    // every getCurrentPosition/requestPermission call re-show the SDK's
    // background-permission rationale dialog (the SDK resets its
    // "didShowRationale" flag on every call, and Samsung's One UI keeps
    // shouldShowRequestPermissionRationale true after denial) — the dialog
    // reappeared on every tab mount and walk start.  We use ACTIVITY_TYPE_OTHER
    // and never read motion-activity data, so disabling is correct.
    disableMotionActivityUpdates: true,

    // ─── iOS settings ──────────────────────────────────────────────────
    disableStopDetection: true,   // Don't detect stops automatically
    pausesLocationUpdatesAutomatically: false, // Don't let iOS pause updates
    preventSuspend: true, // Keep iOS app running in background (with heartbeat)

    // ─── Notification for foreground service (Android) ────────────────
    // A visible, ongoing notification is required for Android foreground
    // services.  If the notification is dismissed or not shown, Samsung and
    // other OEMs will kill the service much more aggressively.
    notification: {
      title: i18n.t('record.notificationTitle'),
      text: i18n.t('record.notificationText'),
      // PRIORITY_MAX makes the notification "priority" which reduces the
      // chance that Samsung's notification-manager will suppress it.
      priority: (BackgroundGeolocation as any).NOTIFICATION_PRIORITY_HIGH ?? 2,
      sticky: true, // Notification cannot be swiped away
    } as any,

    // NOTE: no `backgroundPermissionRationale` — with it configured, the SDK
    // shows its own "Allow access in the background?" dialog whenever any
    // permission in its chain is missing (see disableMotionActivityUpdates
    // above).  Background access is granted via Settings under the 'Any'
    // strategy, so the SDK should never pop this dialog.
  } as any);

  // Enforce "tracking state matches session state" on every init.
  await reconcileTrackingState();
}